import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadServerFile } from "@/lib/download";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, FileSpreadsheet, BarChart3, DollarSign, Users, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    api.get("/reports/performance").then(r => setStats(r.data.stats || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const download = (kind, fmt) => {
    try {
      const params = { fmt };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      downloadServerFile(`/reports/${kind}`, params);
      toast.success(`${kind} ${fmt.toUpperCase()} downloading…`);
    } catch (e) { toast.error("Download failed"); }
  };

  return (
    <Layout title="Reports" subtitle="Generation Hub">
      {/* Date range filter */}
      <div className="soft-card p-5 sm:p-6 mb-6">
        <div className="flex items-center gap-2 eyebrow mb-4">
          <Calendar size={13} /> Date range (optional)
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="eyebrow text-[10px]">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 w-[170px]" data-testid="report-date-from" />
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow text-[10px]">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 w-[170px]" data-testid="report-date-to" />
          </div>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" className="h-10 text-[13px]" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="report-date-clear">
              Clear
            </Button>
          )}
          <div className="text-[12px] text-[#8E8E93] ml-auto">
            Leave blank to include all records
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <ReportCard
          icon={Users}
          title="Team Performance"
          description="Tasks assigned, completed, cycle time, overdue counts and completion rate — per team member."
          onPdf={() => download("performance", "pdf")}
          onExcel={() => download("performance", "excel")}
          testIdPdf="report-performance-pdf"
          testIdExcel="report-performance-excel"
        />
        <ReportCard
          icon={DollarSign}
          title="Expense Ledger"
          description="All logged expenses with category breakdown, totals, and vendor detail — beautifully formatted."
          onPdf={() => download("expenses", "pdf")}
          onExcel={() => download("expenses", "excel")}
          testIdPdf="report-expenses-pdf"
          testIdExcel="report-expenses-excel"
        />
      </div>

      {/* Quick performance preview */}
      <div className="tech-card overflow-hidden rise rise-1">
        <div className="p-5 border-b border-neutral-200 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-neutral-500" />
          <div className="overline">Live performance preview</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <Th>Member</Th>
                <Th className="text-right">Assigned</Th>
                <Th className="text-right">Completed</Th>
                <Th className="text-right">In progress</Th>
                <Th className="text-right">Overdue</Th>
                <Th className="text-right">Avg cycle (h)</Th>
                <Th className="text-right">Completion %</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="py-10 text-center text-neutral-500">Loading…</td></tr>}
              {!loading && stats.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-neutral-500">No data yet.</td></tr>
              )}
              {stats.map(s => {
                const rate = s.assigned ? Math.round((s.completed / s.assigned) * 100) : 0;
                return (
                  <tr key={s.user_id} className="border-b border-neutral-100" data-testid={`perf-row-${s.user_id}`}>
                    <td className="py-3 px-4 font-medium">{s.name}</td>
                    <td className="py-3 px-4 text-right font-mono">{s.assigned}</td>
                    <td className="py-3 px-4 text-right font-mono">{s.completed}</td>
                    <td className="py-3 px-4 text-right font-mono">{s.in_progress}</td>
                    <td className={`py-3 px-4 text-right font-mono ${s.overdue > 0 ? "text-red-700 font-medium" : ""}`}>{s.overdue}</td>
                    <td className="py-3 px-4 text-right font-mono">{s.avg_cycle_hours ? s.avg_cycle_hours.toFixed(1) : "—"}</td>
                    <td className="py-3 px-4 text-right font-mono">{rate}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function ReportCard({ icon: Icon, title, description, onPdf, onExcel, testIdPdf, testIdExcel }) {
  return (
    <div className="tech-card p-6 sm:p-7 rise">
      <div className="flex items-start justify-between">
        <div className="h-10 w-10 rounded-md bg-neutral-950 text-white grid place-items-center">
          <Icon className="h-4.5 w-4.5" strokeWidth={1.6} />
        </div>
        <div className="overline">Report</div>
      </div>
      <h3 className="font-display text-2xl mt-5 font-semibold tracking-tight">{title}</h3>
      <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{description}</p>
      <div className="flex flex-wrap gap-2 mt-6">
        <Button onClick={onPdf} className="bg-neutral-950 hover:bg-neutral-800 text-white" data-testid={testIdPdf}>
          <FileText className="h-4 w-4 mr-1.5" /> Generate PDF
        </Button>
        <Button onClick={onExcel} variant="outline" data-testid={testIdExcel}>
          <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export Excel
        </Button>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-neutral-600 text-left ${className}`}>{children}</th>;
}
